/**
 * Redux slice for account-scoped user management (the gamemaster Users page):
 * list, create, edit, and deactivate/reactivate users in the caller's account.
 *
 * Distinct from `accountsSlice`, which manages users cross-account for admins.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '../types/auth';
import { UserCreate, UserUpdate } from '../types/user';
import usersService from '../services/usersService';

// =============================================================================
// State
// =============================================================================

export interface UsersState {
    users: User[];
    total: number;
    isLoading: boolean;
    error: string | null;
    // Per-row in-flight tracking (edit / deactivate on a given user id).
    mutatingIds: string[];
}

const initialState: UsersState = {
    users: [],
    total: 0,
    isLoading: false,
    error: null,
    mutatingIds: [],
};

// =============================================================================
// Error extraction (shared shape with accountsSlice)
// =============================================================================

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: unknown } } };
        const detail = axiosError.response?.data?.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            const messages = detail.map((err: { msg?: string; loc?: string[] }) => {
                const field = err.loc?.slice(-1)[0] || 'field';
                const msg = err.msg || 'validation error';
                return `${field}: ${msg}`;
            });
            return messages.join('; ') || 'Validation error';
        }
        if (detail && typeof detail === 'object' && 'message' in detail) {
            return String((detail as { message: unknown }).message);
        }
        return 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An error occurred';
}

// =============================================================================
// Async Thunks
// =============================================================================

export const fetchUsers = createAsyncThunk<
    { users: User[]; total: number },
    void,
    { rejectValue: string }
>('users/fetchUsers', async (_, { rejectWithValue }) => {
    try {
        const response = await usersService.getUsers();
        return { users: response.users, total: response.total };
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createUser = createAsyncThunk<
    User,
    UserCreate,
    { rejectValue: string }
>('users/createUser', async (data, { rejectWithValue }) => {
    try {
        return await usersService.createUser(data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const updateUser = createAsyncThunk<
    User,
    { userId: string; data: UserUpdate },
    { rejectValue: string }
>('users/updateUser', async ({ userId, data }, { rejectWithValue }) => {
    try {
        return await usersService.updateUser(userId, data);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Slice
// =============================================================================

function replaceUser(state: UsersState, user: User): void {
    const index = state.users.findIndex((u) => u.id === user.id);
    if (index !== -1) {
        state.users[index] = user;
    }
}

export const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        clearUsersError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch users
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.users = action.payload.users;
                state.total = action.payload.total;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch users';
            });

        // Create user
        builder
            .addCase(createUser.fulfilled, (state, action) => {
                state.users.push(action.payload);
                state.total += 1;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.error = action.payload || 'Failed to create user';
            });

        // Update user (edit / deactivate / reactivate) — per-row in-flight.
        builder
            .addCase(updateUser.pending, (state, action) => {
                if (!state.mutatingIds.includes(action.meta.arg.userId)) {
                    state.mutatingIds.push(action.meta.arg.userId);
                }
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.payload.id
                );
                replaceUser(state, action.payload);
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.mutatingIds = state.mutatingIds.filter(
                    (id) => id !== action.meta.arg.userId
                );
                state.error = action.payload || 'Failed to update user';
            });
    },
});

export const { clearUsersError } = usersSlice.actions;

export default usersSlice.reducer;
